import React from 'react';
import { Sparkles, Wrench, FileSpreadsheet } from 'lucide-react';
import { cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';

// Modes: 'simple', 'advanced', 'import'
export const AnalysisModeToggle = ({ 
  mode = 'simple', // 'simple' | 'advanced' | 'import'
  onModeChange, 
  // Legacy support for isAdvanced/onToggle
  isAdvanced, 
  onToggle,
  className, 
  // 'light' (default) — slate-on-light treatment for the post-Feb-2026
  // light hero canvas. 'dark' is preserved for any legacy dark-themed
  // wrapper that explicitly opts in.
  variant = 'light',
  showImport = true // Whether to show the Import option
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  // Support both old and new API
  const currentMode = mode !== undefined ? mode : (isAdvanced ? 'advanced' : 'simple');
  const handleModeChange = (newMode) => {
    if (onModeChange) {
      onModeChange(newMode);
    } else if (onToggle) {
      // Legacy support
      onToggle(newMode === 'advanced');
    }
  };
  
  // Theme-aware treatment — in dark mode the light slate container +
  // stark-white selected pill clashed with the dark canvas, so we swap
  // to a subtle elevated dark surface. Light mode is untouched.
  const containerStyles = isDark ? 'bg-slate-800/70 ring-1 ring-white/10' : 'bg-slate-200';
  const selectedStyles = isDark ? 'bg-slate-600 text-white shadow-sm' : 'bg-white text-slate-900 shadow-sm';
  const nonSelectedStyles = isDark
    ? 'text-slate-300 hover:text-white hover:bg-slate-700/60'
    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100';

  const btnBase = 'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all';

  return (
    <div className={cn('inline-flex items-center p-1 rounded-lg', containerStyles, className)}>
      <button
        onClick={() => handleModeChange('simple')}
        className={cn(btnBase, currentMode === 'simple' ? selectedStyles : nonSelectedStyles)}
        data-testid="simple-mode-btn"
      >
        <Sparkles className="h-4 w-4" />
        Simple
      </button>
      <button
        onClick={() => handleModeChange('advanced')}
        className={cn(btnBase, currentMode === 'advanced' ? selectedStyles : nonSelectedStyles)}
        data-testid="advanced-mode-btn"
      >
        <Wrench className="h-4 w-4" />
        Advanced
      </button>
      {showImport && (
        <button
          onClick={() => handleModeChange('import')}
          className={cn(btnBase, currentMode === 'import' ? selectedStyles : nonSelectedStyles)}
          data-testid="import-mode-btn"
        >
          <FileSpreadsheet className="h-4 w-4" />
          Import
        </button>
      )}
    </div>
  );
};

export default AnalysisModeToggle;
